import { useEffect, useState } from "react";
import { CalendarPlus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";

const URL_RE = /(https?:\/\/[^\s]+)/;

// The headline and picture behind a shared link, plus a one-tap send to Saturday.
export const LinkPreview = ({ text, mine }) => {
  const url = String(text || "").match(URL_RE)?.[1];
  const [card, setCard] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!url) return;
    let alive = true;
    api
      .get("/news/preview", { params: { url } })
      .then(({ data }) => alive && data.title && setCard(data))
      .catch(() => {});
    return () => { alive = false; };
  }, [url]);

  if (!url || !card) return null;

  const keepForSaturday = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/news/weekend/add", { url, title: card.title });
      setSaved(true);
      toast.success(data.read_min ? `Added to Saturday · ${data.read_min} min read`
                                  : "Added to your Saturday list");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not add that");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="link-preview"
      className={`mt-2 w-full overflow-hidden rounded-2xl border ${
        mine ? "border-white/25 bg-black/15" : "border-white/10 bg-black/20"}`}
    >
      <a href={card.url} target="_blank" rel="noreferrer" className="block">
        {card.image && (
          <img src={card.image} alt="" loading="lazy"
               className="h-[74px] w-full object-cover" data-testid="link-preview-image" />
        )}
        <div className="p-2">
          <p className="text-[9px] uppercase tracking-wide opacity-70">{card.site}</p>
          <p className="mt-0.5 text-[12px] font-semibold leading-snug" dir="auto">{card.title}</p>
          {card.blurb && (
            <p className="mt-0.5 line-clamp-2 text-[10px] opacity-80" dir="auto">{card.blurb}</p>
          )}
        </div>
      </a>
      <button
        data-testid="link-preview-saturday"
        onClick={keepForSaturday}
        disabled={saving || saved}
        className="flex w-full items-center gap-1.5 border-t border-white/10 px-2 py-1.5 text-[9px] font-semibold transition-colors hover:bg-white/10 disabled:opacity-60"
      >
        {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <CalendarPlus className="h-3 w-3" />}
        {saved ? "On your Saturday list" : "Read it Saturday"}
      </button>
    </div>
  );
};
